import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';

import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import type { RootStackParamList } from '../navigation/stackTypes';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import { showThemedAlert } from '../components/common/ThemedAlert';
import { isSuccessful } from '../services/api-requests';
import {
  getDefaultPaymentMethod,
  removeDefaultPaymentMethod,
} from '../services/payment-method-service';
import { listRfidCards } from '../services/rfid-service';
import {
  PaymentMethodSummary,
  formatCardBrand,
  formatExpiry,
} from '../models/payment-method-model';
import { notificationService } from '../services/notification-service';
// @ts-ignore - Platform-specific file (.native/.web)
import { useDefaultPaymentSetup } from '../hooks/useDefaultPaymentSetup';

type Props = NativeStackScreenProps<RootStackParamList, 'PaymentMethods'>;

interface SetupFormProps {
  onSuccess: (pm: PaymentMethodSummary) => void;
  onCancel: () => void;
  persistDefaultPaymentMethod: (
    paymentMethodId: string,
  ) => Promise<PaymentMethodSummary | null>;
}

const SetupForm: React.FC<SetupFormProps> = ({
  onSuccess,
  onCancel,
  persistDefaultPaymentMethod,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.origin },
      });
      if (error) {
        notificationService.sendNotification(
          'error',
          error.message ?? 'Card setup failed.',
        );
        return;
      }
      const pmId = setupIntent?.payment_method;
      if (!pmId || typeof pmId !== 'string') {
        notificationService.sendNotification(
          'error',
          'Card setup did not return a payment method.',
        );
        return;
      }
      const result = await persistDefaultPaymentMethod(pmId);
      if (result) onSuccess(result);
    } finally {
      setSubmitting(false);
    }
  }, [stripe, elements, onSuccess, persistDefaultPaymentMethod]);

  return (
    <View style={styles.setupForm}>
      <div
        style={{
          backgroundColor: '#1A1D21',
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
        }}
      >
        <PaymentElement />
      </div>
      <View style={styles.actionRow}>
        <LoadingButton
          onPress={handleSubmit}
          isLoading={submitting}
          backgroundColor="#40BFB4"
          style={styles.actionButton}
        >
          Save card
        </LoadingButton>
        <Pressable
          onPress={onCancel}
          style={({ pressed }) => [
            styles.cancelBtn,
            { opacity: pressed ? 0.6 : 1 },
          ]}
        >
          <Text style={styles.cancelBtnText}>Cancel</Text>
        </Pressable>
      </View>
    </View>
  );
};

const PaymentMethods: React.FC<Props> = () => {
  const [pm, setPm] = useState<PaymentMethodSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [removing, setRemoving] = useState<boolean>(false);
  const [hasActiveCard, setHasActiveCard] = useState<boolean>(false);

  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const { beginSetup, persistDefaultPaymentMethod, stripePromise } =
    useDefaultPaymentSetup();

  const fetchData = useCallback(async () => {
    try {
      const [pmResp, cardsResp] = await Promise.all([
        getDefaultPaymentMethod(),
        listRfidCards(),
      ]);
      if (isSuccessful(pmResp.status)) {
        setPm(pmResp.payload?.default_payment_method ?? null);
      }
      if (isSuccessful(cardsResp.status) && cardsResp.payload) {
        setHasActiveCard(
          (cardsResp.payload.cards ?? []).some((c) => c.status === 'ACTIVE'),
        );
      }
    } catch (e) {
      console.error('[PaymentMethods.web] fetch error:', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    })();
  }, [fetchData]);

  const onAddOrReplace = useCallback(async () => {
    const result = await beginSetup();
    if (result) setClientSecret(result.clientSecret);
  }, [beginSetup]);

  const onRemove = useCallback(() => {
    showThemedAlert(
      'Remove default payment method?',
      hasActiveCard
        ? 'Without a default payment method, RFID taps will be declined.'
        : 'Without a default payment method, RFID tap-to-charge will not work.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            setRemoving(true);
            try {
              const resp = await removeDefaultPaymentMethod();
              if (isSuccessful(resp.status)) {
                setPm(null);
                notificationService.sendNotification(
                  'success',
                  'Default payment method removed.',
                );
              } else {
                notificationService.sendNotification(
                  'error',
                  'Could not remove payment method.',
                );
              }
            } finally {
              setRemoving(false);
            }
          },
        },
      ],
      'warning',
    );
  }, [hasActiveCard]);

  const showRequiredBanner = hasActiveCard && !pm;

  const elementsOptions = useMemo(
    () =>
      clientSecret
        ? ({
            clientSecret,
            appearance: {
              theme: 'night' as const,
              variables: {
                colorPrimary: '#40BFB4',
                colorBackground: '#1A1D21',
                colorText: '#FFFFFF',
                colorDanger: '#EF4444',
              },
            },
          })
        : undefined,
    [clientSecret],
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <Header headerText="Payment Methods" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
      >
        {showRequiredBanner && (
          <View style={styles.banner}>
            <MaterialIcons name="info" size={20} color="#F59E0B" />
            <Text style={styles.bannerText}>
              Required for tap-to-charge. Add a default payment method to
              authorize RFID taps.
            </Text>
          </View>
        )}

        <Text style={styles.sectionTitle}>Default payment method</Text>

        {loading ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color="#40BFB4" />
          </View>
        ) : pm ? (
          <View style={styles.card}>
            <View style={styles.cardRow}>
              <View style={styles.cardIcon}>
                <MaterialCommunityIcons
                  name="credit-card"
                  size={22}
                  color="#40BFB4"
                />
              </View>
              <View style={styles.cardCopy}>
                <Text style={styles.cardBrand}>
                  {formatCardBrand(pm.brand)} ••{pm.last4}
                </Text>
                <Text style={styles.cardExpiry}>
                  Expires {formatExpiry(pm.exp_month, pm.exp_year)}
                </Text>
              </View>
            </View>
            <View style={styles.actionRow}>
              <LoadingButton
                onPress={onAddOrReplace}
                backgroundColor="#40BFB4"
                style={styles.actionButton}
              >
                Replace card
              </LoadingButton>
              <LoadingButton
                onPress={onRemove}
                isLoading={removing}
                backgroundColor="#3A3D42"
                style={styles.actionButton}
              >
                Remove
              </LoadingButton>
            </View>
          </View>
        ) : (
          <View style={styles.card}>
            <View style={styles.emptyState}>
              <MaterialCommunityIcons
                name="credit-card-off-outline"
                size={36}
                color="#8D8E90"
              />
              <Text style={styles.emptyTitle}>No card on file</Text>
              <Text style={styles.emptySubtitle}>
                Add a card to enable RFID tap-to-charge across every Meander
                station you can use.
              </Text>
            </View>
            <LoadingButton
              onPress={onAddOrReplace}
              backgroundColor="#40BFB4"
              style={styles.singleButton}
            >
              Add a card
            </LoadingButton>
          </View>
        )}

        {clientSecret && elementsOptions && (
          <Elements stripe={stripePromise} options={elementsOptions}>
            <SetupForm
              onSuccess={(saved) => {
                setPm(saved);
                setClientSecret(null);
              }}
              onCancel={() => setClientSecret(null)}
              persistDefaultPaymentMethod={persistDefaultPaymentMethod}
            />
          </Elements>
        )}

        <Text style={styles.footnote}>
          We never store your full card number. Cards are tokenized by Stripe
          and used only to authorize charges from your RFID taps.
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#1A1D21' },
  scroll: { flex: 1 },
  scrollContent: { paddingHorizontal: 16, paddingBottom: 40 },
  banner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: '#3A2E14',
    borderRadius: 12,
    padding: 14,
    borderLeftWidth: 3,
    borderLeftColor: '#F59E0B',
    marginTop: 12,
    marginBottom: 16,
  },
  bannerText: {
    flex: 1,
    color: '#FFE9C7',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 18,
  },
  sectionTitle: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginTop: 8,
    marginBottom: 8,
  },
  card: { backgroundColor: '#292D33', borderRadius: 14, padding: 16 },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  cardIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#40BFB420',
  },
  cardCopy: { flex: 1 },
  cardBrand: {
    color: '#FFFFFF',
    fontSize: 17,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
  },
  cardExpiry: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 2,
  },
  actionRow: { flexDirection: 'row', gap: 10 },
  actionButton: { flex: 1, borderRadius: 12, paddingVertical: 4 },
  singleButton: { borderRadius: 12, paddingVertical: 4 },
  emptyState: { alignItems: 'center', paddingVertical: 20, paddingHorizontal: 8 },
  emptyTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginTop: 12,
  },
  emptySubtitle: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    textAlign: 'center',
    lineHeight: 18,
    marginTop: 6,
    marginBottom: 16,
  },
  centered: { paddingVertical: 60, alignItems: 'center' },
  setupForm: {
    backgroundColor: '#292D33',
    borderRadius: 14,
    padding: 16,
    marginTop: 16,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3A3D42',
  },
  cancelBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
  },
  footnote: {
    color: '#6C6F75',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 16,
    marginTop: 20,
    textAlign: 'center',
  },
});

export default PaymentMethods;
